/**
 * ProviderSchemaAssignmentsSection — list / add / remove schema assignments.
 * Refreshes parent provider after mutations so header activate-guards stay in sync.
 */

import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button, AlertMessage, ConfirmDialog, Spinner } from "@/components/reusable";
import { SchemaStatusBadge } from "@/components/schema/SchemaStatusBadge";
import {
  fetchProvider,
  fetchSchemaAssignments,
  removeSchemaAssignment,
  addSchemaAssignment,
} from "@/services/providersApi";
import { listSchemas, toAssignableTargetSchemas } from "@/services/schemaBuilderApi";
import { useTelemetry } from "@/hooks/useTelemetry";
import { cn } from "@/lib/utils";
import type { ProviderOut, SchemaAssignmentOut } from "@/types/providers";
import type { TargetSchema } from "@/types";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

interface ProviderSchemaAssignmentsSectionProps {
  provider: ProviderOut;
  isAdmin: boolean;
  onProviderUpdated: (updated: ProviderOut) => void;
}

/** Schema assignment table + add/remove controls for the Overview tab. */
export function ProviderSchemaAssignmentsSection({
  provider,
  isAdmin,
  onProviderUpdated,
}: ProviderSchemaAssignmentsSectionProps) {
  const { track, trackError } = useTelemetry();

  const [assignments, setAssignments] = useState<SchemaAssignmentOut[]>([]);
  const [allSchemas, setAllSchemas] = useState<TargetSchema[]>([]);
  const [loadingAssignments, setLoadingAssignments] = useState(true);
  const [addingSchemaId, setAddingSchemaId] = useState("");
  const [savingAdd, setSavingAdd] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState<SchemaAssignmentOut | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [assignmentError, setAssignmentError] = useState<string | null>(null);

  useEffect(() => {
    setLoadingAssignments(true);
    Promise.all([fetchSchemaAssignments(provider.provider_id), listSchemas()])
      .then(([asgn, schemas]) => {
        setAssignments(asgn);
        setAllSchemas(toAssignableTargetSchemas(schemas));
      })
      .catch((err) => trackError(err, { context: "overview_tab_load" }))
      .finally(() => setLoadingAssignments(false));
  }, [provider.provider_id, trackError]);

  const assignedIds = new Set(assignments.map((a) => a.schema_id));
  const availableToAdd = allSchemas.filter((s) => !assignedIds.has(s.id));
  const removingLast = assignments.length === 1;

  async function handleAddSchema() {
    if (!addingSchemaId) return;
    setSavingAdd(true);
    setAssignmentError(null);
    try {
      await addSchemaAssignment(provider.provider_id, addingSchemaId);
      const updated = await fetchSchemaAssignments(provider.provider_id);
      setAssignments(updated);
      const refreshed = await fetchProvider(provider.provider_id);
      onProviderUpdated(refreshed);
      track("provider_schema_assigned", {
        providerId: provider.provider_id,
        schemaId: addingSchemaId,
      });
      setAddingSchemaId("");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to add schema.";
      setAssignmentError(msg);
      trackError(err instanceof Error ? err : new Error(msg), { context: "add_schema_assignment" });
    } finally {
      setSavingAdd(false);
    }
  }

  async function handleRemoveSchema() {
    if (!removeConfirm) return;
    setRemovingId(removeConfirm.schema_id);
    setAssignmentError(null);
    try {
      await removeSchemaAssignment(provider.provider_id, removeConfirm.schema_id);
      setAssignments((prev) => prev.filter((a) => a.schema_id !== removeConfirm.schema_id));
      const refreshed = await fetchProvider(provider.provider_id);
      onProviderUpdated(refreshed);
      track("provider_schema_removed", {
        providerId: provider.provider_id,
        schemaId: removeConfirm.schema_id,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to remove schema.";
      setAssignmentError(msg);
      trackError(err instanceof Error ? err : new Error(msg), {
        context: "remove_schema_assignment",
      });
    } finally {
      setRemovingId(null);
      setRemoveConfirm(null);
    }
  }

  return (
    <section>
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-[var(--color-text-tertiary)]">
        Schema Assignments
      </h2>

      {assignmentError && (
        <div className="mb-3">
          <AlertMessage variant="error" message={assignmentError} />
        </div>
      )}

      {loadingAssignments ? (
        <div className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
          <Spinner size="sm" />
          Loading…
        </div>
      ) : assignments.length === 0 ? (
        <p className="text-sm text-[var(--color-text-tertiary)] italic">No schemas assigned yet.</p>
      ) : (
        <div className="mb-4 overflow-hidden rounded-xl border border-[var(--color-border-light)]">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-bg-secondary)]">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-[var(--color-text-secondary)]">
                  Schema
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-[var(--color-text-secondary)]">
                  Assigned
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-[var(--color-text-secondary)]">
                  Assigned By
                </th>
                {isAdmin && <th className="w-12 px-4 py-2.5" />}
              </tr>
            </thead>
            <tbody>
              {assignments.map((a, idx) => (
                <tr
                  key={a.schema_id}
                  className={cn(
                    "transition-colors",
                    idx > 0 && "border-t border-[var(--color-border-light)]"
                  )}
                >
                  <td className="px-4 py-2.5 font-medium text-[var(--color-text-primary)]">
                    {a.schema_name ?? a.schema_id}
                  </td>
                  <td className="px-4 py-2.5 text-[var(--color-text-secondary)]">
                    {formatDate(a.assigned_at)}
                  </td>
                  <td className="px-4 py-2.5 text-[var(--color-text-secondary)]">
                    {a.assigned_by ?? "—"}
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => setRemoveConfirm(a)}
                        disabled={removingId === a.schema_id}
                        className="rounded-md p-1.5 text-[var(--color-text-tertiary)] transition-colors hover:bg-red-50 hover:text-[var(--color-error)] disabled:opacity-40"
                        aria-label={`Remove ${a.schema_name ?? a.schema_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isAdmin && availableToAdd.length > 0 && (
        <div className="flex items-center gap-2">
          <select
            value={addingSchemaId}
            onChange={(e) => setAddingSchemaId(e.target.value)}
            className="h-9 flex-1 rounded-md border border-[var(--color-border)] bg-white px-3 text-sm outline-none transition focus:border-[var(--color-action-primary)]"
            disabled={savingAdd}
          >
            <option value="">Select a schema to add…</option>
            {availableToAdd.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
                {s.status === "preset" ? " (Preset)" : " (Custom)"}
              </option>
            ))}
          </select>
          {addingSchemaId && (
            <SchemaStatusBadge
              status={availableToAdd.find((s) => s.id === addingSchemaId)?.status}
            />
          )}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleAddSchema}
            disabled={!addingSchemaId || savingAdd}
          >
            <Plus className="h-4 w-4" />
            {savingAdd ? "Adding…" : "Add"}
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={!!removeConfirm}
        onOpenChange={(open) => {
          if (!open && !removingId) setRemoveConfirm(null);
        }}
        title="Remove schema assignment?"
        description={
          removingLast
            ? `Remove "${removeConfirm?.schema_name ?? removeConfirm?.schema_id}" from this provider? This is the last assigned schema — uploads and activation will fail until another schema is assigned.`
            : `Remove "${removeConfirm?.schema_name ?? removeConfirm?.schema_id}" from this provider? The assignment will be deactivated but its history is retained.`
        }
        confirmLabel="Remove"
        confirmLoadingLabel="Removing…"
        confirmLoading={!!removingId}
        tone="warning"
        onConfirm={handleRemoveSchema}
      />
    </section>
  );
}
